import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";
import { RNHFeriadoSerieGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoSerieGet";
import { getMonthStart, monthKey, esCampoDia } from "../utils/fechas";

/** Nacional. Si caen dos el mismo día, es el que manda para el tinte. */
const TIPO_NACIONAL = "N";

/**
 * La fecha del servicio, normalizada a la MISMA forma que el `field` de la columna.
 *
 * Se corta a 10 caracteres en vez de parsear: en este back algunas fechas vienen con hora
 * ("2026-09-14 11:03:59" en `usuarioVinculoUFH`), y si `rnhFeriadoSerieFecha` llegara así,
 * la key del mapa no matchearía nunca con el field y el feriado no se marcaría — sin error,
 * sin nada: simplemente no pasa nada, que es el peor modo de fallar.
 *
 * Devuelve "" si lo que queda no tiene forma de fecha, para no meter basura en el mapa.
 */
const aClaveDeDia = (valor) => {
    const recortada = String(valor ?? "").slice(0, 10);
    return esCampoDia(recortada) ? recortada : "";
};

/**
 * Los feriados del mes que muestra la grilla, indexados por fecha.
 *
 *   { "2026-05-01": { tipo: "N", nombres: ["Día del Trabajador"] } }
 *
 * La key es `YYYY-MM-DD` a propósito: es exactamente el `field` de las columnas-día, así
 * que marcar una columna es un lookup directo y no hay que traducir entre índice y fecha.
 *
 * Vive aparte de `cargarHorarios` por dos motivos. Los feriados dependen del MES y no del
 * hospital, así que meterlos ahí los re-pediría en cada cambio de hospital; y además
 * `cargarHorarios` corta con `if (!hospitalId) return`, o sea que la rama sin hospitales
 * se quedaría sin feriados.
 *
 * El `cache` es por mes: ir y volver entre meses no vuelve a pegarle al servicio.
 */
export function useFeriadosDelMes(monthStart) {
    const credentials = useRecoilValue(userCredentials);
    const [feriados, setFeriados] = useState({});
    const [cargando, setCargando] = useState(false);
    const cacheRef = useRef({});

    // El efecto depende de la CLAVE del mes y no de `monthStart`: ese es un objeto moment
    // nuevo en cada render, así que como dependencia dispararía el efecto siempre.
    const clave = monthKey(monthStart);

    useEffect(() => {
        // Si el mes cambia mientras la request está en vuelo, lo que vuelva es de un mes
        // que ya no se está mirando: se descarta en vez de pisar el estado.
        let cancelado = false;

        (async () => {
            const enCache = cacheRef.current[clave];
            if (enCache) {
                setFeriados(enCache);
                return;
            }

            setCargando(true);
            const inicio = getMonthStart(monthStart);
            const respuesta = await RNHFeriadoSerieGet(credentials, {
                desde: inicio.format("YYYY-MM-DD"),
                hasta: moment(inicio).endOf("month").format("YYYY-MM-DD"),
            });

            if (cancelado) return;
            setCargando(false);

            if (!respuesta.ok) return;

            const mapa = {};
            respuesta.data.forEach((f) => {
                const fecha = aClaveDeDia(f?.rnhFeriadoSerieFecha);
                if (!fecha) return;

                const tipo = f?.rnhFeriadoTipo ?? "";
                const nombre = f?.rnhFeriadoNombre ?? "";

                if (!mapa[fecha]) {
                    mapa[fecha] = { tipo, nombres: nombre ? [nombre] : [] };
                    return;
                }

                // Dos feriados el mismo día (un nacional y un provincial pueden coincidir):
                // para el TINTE gana el nacional, pero los dos nombres se conservan porque
                // el tooltip los muestra a ambos.
                if (nombre) mapa[fecha].nombres.push(nombre);
                if (tipo === TIPO_NACIONAL) mapa[fecha].tipo = TIPO_NACIONAL;
            });

            cacheRef.current[clave] = mapa;
            setFeriados(mapa);
        })();

        return () => {
            cancelado = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave]);

    return { feriados, cargando };
}
